import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  LogOut,
  AlertTriangle,
  Briefcase,
  ChevronDown,
  KeyRound,
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import AlertBell from './AlertBell';
import GlobalSearch from './GlobalSearch';
import { HELP_ITEM, navFor } from '../lib/nav';
import type { NavItem } from '../lib/nav';
import { isAllCompanies } from '../lib/companyScope';
import AlertWatcher from './AlertWatcher';
import PingWatcher from './PingWatcher';
import ViewingAsBar from './ViewingAsBar';
import {
  ROLE_LABELS,
  canSeeAllCompanies,
  useAuth,
} from '../store/auth';
import { useImpersonation } from '../store/impersonation';
import { titleCaseName } from '../lib/titleCase';
import ActingCompanyPicker from './ActingCompanyPicker';


/**
 * The shell's own width, used by every part of it.
 *
 * **Why this is a constant and not four class strings.** Widening the table
 * pages has been tried once and reverted -- `5d7aa3f`, undone by `f96e0f6`,
 * written up as BACKLOG entry 5. It worked mechanically and looked wrong,
 * because only the *content* came out of the cap: the header, the nav strip
 * and the all-companies banner stayed at 72rem, so a wide table no longer
 * lined up with the bar above it and the screen read as a different product.
 * The revert message is explicit that the reading width was load-bearing for
 * the layout rather than for prose.
 *
 * So the fix is the half that attempt skipped: the chrome moves with the
 * content, and there is one string for both. Four copies is how they came to
 * disagree in the first place.
 *
 * Uncapped rather than a wider cap. These are 18- and 33-column tables whose
 * whole problem is horizontal scrolling, and every pixel of gutter is a column
 * somebody has to scroll to reach. `px-6` keeps the content off the window
 * edge without spending 250px a side on nothing.
 */
const SHELL_WIDTH = 'w-full px-6';

/**
 * Shown while every company is in view at once.
 *
 * "Nothing chosen" no longer needs a banner: RequireCompany replaces the page
 * itself with a prompt, which is louder and lands where somebody is already
 * looking. This is for the other state, which has no such tell — the rows look
 * exactly like one company's rows, and a total is a total whether it came from
 * one tenant or four.
 *
 * That was the old default, and it is why a report opened with 35,032
 * candidates to call back. It is fine to read this way on purpose. It needs
 * saying that you are.
 */
function AllCompaniesBanner({ wide }: { wide: boolean }) {
  return (
    <div className="border-b border-amber-300 bg-amber-100">
      {/* Follows whatever the page below it is doing, so the warning lines up
          with the content it is warning about. The paragraph keeps its own cap
          either way -- a sentence 1,900 pixels long is unreadable however wide
          the table under it wants to be. */}
      <div
        className={`flex items-start gap-2 py-2.5 ${
          wide ? SHELL_WIDTH : 'mx-auto max-w-6xl px-4'
        }`}
      >
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        <p className="max-w-5xl text-xs text-amber-900">
          <strong>All companies.</strong> Everything below is drawn from every
          company at once, so counts and totals span all of them. Nothing can be
          created here — a record has to belong to one company. Pick one from
          the bar above to work in it.
        </p>
      </div>
    </div>
  );
}

/**
 * One group of tabs, as a dropdown.
 *
 * Wrapping instead of scrolling: the old bar put twenty-odd tabs in a
 * horizontal scroller, so on a laptop the last third of them existed only if
 * you knew to drag sideways.
 *
 * The button reads as active whenever the open route belongs to it, so the
 * current screen is findable without opening anything — a collapsed group
 * that gives no sign it holds the active page is worse than a long bar.
 */
function NavGroupMenu({ label, items }: { label: string; items: NavItem[] }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const container = useRef<HTMLDivElement>(null);

  const holdsCurrentPage = items.some(
    (item) => pathname === item.to || pathname.startsWith(`${item.to}/`),
  );

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="relative" ref={container}>
      <button
        type="button"
        className={`flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5
                    text-sm font-medium transition-colors ${
                      holdsCurrentPage
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-900'
                    }`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((was) => !was)}
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-30 mt-0.5 min-w-48 rounded-lg border border-gray-200
                     bg-white py-1 shadow-lg"
        >
          {items.map(({ to, label: itemLabel, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              role="menuitem"
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-indigo-50 font-medium text-indigo-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`
              }
            >
              <Icon className="h-4 w-4" />
              {itemLabel}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}


export default function AppLayout() {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const actingCompanyId = useAuth((state) => state.actingCompanyId);

  const viewing = useImpersonation((s) => s.target);
  const endedBecause = useImpersonation((s) => s.endedBecause);
  const clearEnded = useImpersonation((s) => s.clearEnded);

  /**
   * Say why a view stopped, when it stopped on its own.
   *
   * Only for target_signed_in. Exiting deliberately needs no announcement —
   * the bar disappearing is the confirmation — but a view ending underneath
   * somebody does, and the wrong message is the dangerous one: the raw 401
   * says "you were signed out because this account signed in on another
   * machine", which is true of the account being viewed and alarming nonsense
   * about the person reading it. Their own session never moved.
   */
  useEffect(() => {
    if (endedBecause === null || endedBecause === 'exited') return;
    toast.info(
      endedBecause === 'target_signed_in'
        ? 'The view ended because that person signed in. Your own session is '
          + 'unaffected.'
        : 'The view expired after 30 minutes. Your own session is unaffected.',
      { duration: 8000 },
    );
    clearEnded();
  }, [endedBecause, clearEnded]);
  const nav = navFor(user);
  const topLevel = nav.topLevel;
  // Grouped navigation is where Admin lives. Hidden entirely while viewing:
  // the admin surface is refused server-side, so leaving the menus up would
  // offer a set of doors that all answer 403.
  const groups = viewing ? [] : nav.groups;

  return (
    <div className="flex h-screen min-h-screen min-h-0 flex-col overflow-hidden">
      {/* First child of the column, so the header and everything below it are
          inset rather than sliding underneath. */}
      <ViewingAsBar />
      {/* The whole header, on every page including the two dashboards.

          They used to have a left rail and a strip of their own instead,
          which made the product two navigations: a tab bar everywhere and
          an accordion rail on /my-day and /admin/home. One bar is the
          decision now, so the rail is gone and this renders unconditionally.

          Nothing that strip carried was dropped. Search and the alert bell
          existed nowhere else and now sit here, which puts them on every
          page rather than on two; Help came off the rail into the user
          block; the company picker and sign-out were already here. */}
      <header className="border-b border-gray-200 bg-white">
        <div className={`flex items-center justify-between py-3 ${SHELL_WIDTH}`}>
          <div className="flex items-center gap-2">
            {/* Always "/", never a computed destination. HomeRoute decides
                where that lands, so the rule lives in one place rather than
                here as well — a second copy is how the link and the route
                start disagreeing about where home is. */}
            <NavLink
              to="/"
              className="text-base font-semibold tracking-tight text-gray-900 hover:text-indigo-700"
              aria-label="Go to home"
            >
              BNB India
            </NavLink>
          </div>

          {/* Search sits between the brand and the controls, which is where
              it sat on the strip this replaces. Capped rather than greedy: it
              is a jump-to, not the page's own search, and a full-width box
              would read as one. */}
          <div className="mx-4 hidden min-w-0 flex-1 sm:block sm:max-w-xs">
            <GlobalSearch />
          </div>

          <div className="flex items-center gap-3">
            {/* Hidden while viewing. You are looking at their app, and
                offering your own controls inside it is how somebody changes a
                setting believing they are in their own account. */}
            {canSeeAllCompanies(user) && !viewing && <ActingCompanyPicker />}

            <AlertBell />

            {/* Help, off the rail. A link rather than a tab: the tabs are the
                day's work in the order it runs, and reading the manual is not
                one of the jobs. Every role the rail offered it to reaches it
                here -- it carried no role flag there and carries none now. */}
            <NavLink
              to={HELP_ITEM.to}
              className="btn-ghost"
              aria-label="Help"
              title="Help"
            >
              <HELP_ITEM.icon className="h-4 w-4" />
            </NavLink>

            {/* The change-password form lives on My Profile and is the only
                one. A second copy here would be a second thing to keep in
                step with the password rules, so this is a link. */}
            <button
              type="button"
              className="btn-ghost"
              onClick={() => navigate('/profile')}
              aria-label="Change your password"
              title="Change your password"
            >
              <KeyRound className="h-4 w-4" />
            </button>

            <div className="text-right">
              <p className="text-sm font-medium text-gray-900">{titleCaseName(user?.fullName)}</p>
              <p className="text-xs text-gray-500">
                {user ? ROLE_LABELS[user.role] : ''}
              </p>
            </div>
            <button
              type="button"
              className="btn-ghost"
              onClick={async () => {
                await logout();
                navigate('/login', { replace: true });
              }}
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>

        <nav className={`flex flex-wrap items-center gap-1 ${SHELL_WIDTH}`}>
          {topLevel
            .filter((item) => !viewing || !item.to.startsWith('/admin'))
            .map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm
                 font-medium transition-colors ${
                   isActive
                     ? 'border-indigo-600 text-indigo-600'
                     : 'border-transparent text-gray-500 hover:text-gray-900'
                 }`
              }
            >
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}

          {groups.length > 0 && (
            <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
          )}

          {groups.map((group) =>
            group.asTab ? (
              <NavLink
                key={group.label}
                to={group.items[0].to}
                className={({ isActive }) =>
                  `flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm
                   font-medium transition-colors ${
                     isActive
                       ? 'border-indigo-600 text-indigo-600'
                       : 'border-transparent text-gray-500 hover:text-gray-900'
                   }`
                }
              >
                <Briefcase className="h-4 w-4" />
                {group.label}
              </NavLink>
            ) : (
              <NavGroupMenu
                key={group.label}
                label={group.label}
                items={group.items}
              />
            ),
          )}
        </nav>
      </header>

      {/* `wide` said "this is a rail page, so run to the window edge". There
          are no rail pages now and every route takes the same inset, so the
          banner matches the shell rather than being told which kind of page
          it is on. */}
      {canSeeAllCompanies(user) && isAllCompanies(actingCompanyId)
        && <AllCompaniesBanner wide={false} />}

      <main className="min-h-0 flex-1 overflow-y-auto">
        {/* One inset for every route now. The dashboards took none of their
            own because a shared one pushed their rail off the top and left
            edges; with no rail there is nothing to run flush to a corner, and
            two padding rules for one shell was the cost of that exception. */}
        <div className={`py-6 ${SHELL_WIDTH}`}>
          <Outlet />
        </div>
      </main>

      {/* Polls for a due check-in and blocks the screen when one fires. */}
      <PingWatcher />
      {/* Mounted after PingWatcher and suppressed while its modal is up:
          the check-in owns the screen when it is showing. */}
      <AlertWatcher />
      <Toaster richColors position="bottom-right" />
    </div>
  );
}
